import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Homepage from "./Homepage";
import MeetDrPonce from "./MeetDrPonce";
import Telemedicine from "./Telemedicine";
import QuestionsAndFears from "./QuestionsAndFears";
import Treatments from "./Treatments";
import Prevention from "./Prevention";
import InsuranceAndPayments from "./InsuranceAndPayments";
import ContactUs from "./ContactUs";
import OurPatientsThoughts from "./OurPatientsThoughts";
import ThisIsUs from "./ThisIsUs";
import WhatWeDo from "./WhatWeDo";
import WhatWeDoTreatments from "./WhatWeDoTreatments";
import WeightManagement from "./WeightManagement";
import CardiacRehabilitation from "./CardiacRehabilitation";

const PageLayout = styled.div`
  display: flex;
  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div<{ isVisible: boolean }>`
  width: 250px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border-radius: 5px;

  @media (max-width: 700px) {
    display: ${({ isVisible }) => (isVisible ? "flex" : "none")};
    width: 100%;
  }
`;

const MenuLink = styled(Link)`
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  color: #333;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: rgb(197 59 108);
  }

  &.active {
    color: rgb(197 59 108);
    font-weight: 700;
  }
`;

const SubMenu = styled.div`
  padding-left: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SubMenuLink = styled(Link)`
  font-size: 0.95rem;
  font-weight: 400;
  margin: 0;
  color: #333;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: rgb(197 59 108);
  }

  &.active {
    color: rgb(197 59 108);
    font-weight: 600;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 1rem;
`;

const Menu: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [showWhatWeDo, setShowWhatWeDo] = useState(false);
  const [showVideoLibrary, setShowVideoLibrary] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const toggleHandler = () => {
      setIsMobileMenuOpen((prev) => !prev);
    };
    document.addEventListener("toggle-menu", toggleHandler);
    return () => document.removeEventListener("toggle-menu", toggleHandler);
  }, []);

  // Close mobile menu on navigation
  const handleMenuClick = () => {
    if (window.innerWidth <= 700) {
      setIsMobileMenuOpen(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 700) {
        setIsMobileMenuOpen(false); // Close menu on mobile
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // Optional: auto-expand submenus if current route matches
  useEffect(() => {
    if (
      location.pathname.startsWith("/whatWeDo") ||
      location.pathname.startsWith("/whatWeDoTreatments") ||
      location.pathname.startsWith("/telemedicine") ||
      location.pathname.startsWith("/weightManagement") ||
      location.pathname.startsWith("/cardiacRehabilitation")
    ) {
      setShowWhatWeDo(true);
    } else {
      setShowWhatWeDo(false);
    }

    if (
      location.pathname.startsWith("/questionsAndFears") ||
      location.pathname.startsWith("/treatments") ||
      location.pathname.startsWith("/prevention")
    ) {
      setShowVideoLibrary(true);
    } else {
      setShowVideoLibrary(false);
    }
  }, [location.pathname]);

  return (
    <PageLayout className="PageLayout">
      <Sidebar isVisible={isMobileMenuOpen || window.innerWidth > 700}>
        <MenuLink
          to="/"
          onClick={handleMenuClick}
          className={location.pathname === "/" ? "active" : ""}
        >
          {t("menu.home")}
        </MenuLink>

        <MenuLink
          to="/meetDrPonce"
          onClick={handleMenuClick}
          className={location.pathname === "/meetDrPonce" ? "active" : ""}
        >
          {t("menu.meetDrPonce")}
        </MenuLink>

        <MenuLink
          to="/whatWeDo"
          onClick={(e) => {
            e.preventDefault();
            setShowWhatWeDo((prev) => !prev);
            navigate("/whatWeDo");
            handleMenuClick();
          }}
          className={location.pathname === "/whatWeDo" ? "active" : ""}
          style={{ userSelect: "none" }}
        >
          {t("menu.WhatWeDo")}
        </MenuLink>
        {showWhatWeDo && (
          <SubMenu>
            <SubMenuLink
              to="/whatWeDoTreatments"
              onClick={handleMenuClick}
              className={
                location.pathname === "/whatWeDoTreatments" ? "active" : ""
              }
            >
              {t("whatWeDo.treatments.title")}
            </SubMenuLink>
            <SubMenuLink
              to="/telemedicine"
              onClick={handleMenuClick}
              className={location.pathname === "/telemedicine" ? "active" : ""}
            >
              {t("menu.telemedicine")}
            </SubMenuLink>
            <SubMenuLink
              to="/weightManagement"
              onClick={handleMenuClick}
              className={
                location.pathname === "/weightManagement" ? "active" : ""
              }
            >
              {t("menu.weightManagement")}
            </SubMenuLink>
            <SubMenuLink
              to="/cardiacRehabilitation"
              onClick={handleMenuClick}
              className={
                location.pathname === "/cardiacRehabilitation" ? "active" : ""
              }
            >
              {t("menu.cardiacRehabilitation")}
            </SubMenuLink>
          </SubMenu>
        )}

        <MenuLink
          to="/thisIsUs"
          onClick={handleMenuClick}
          className={location.pathname === "/thisIsUs" ? "active" : ""}
        >
          {t("menu.thisIsUs")}
        </MenuLink>

        {/* <MenuItem
          onClick={() => setShowVideoLibrary((prev) => !prev)}
          style={{ userSelect: "none" }}
        >
          {t("menu.yourHeartVideoLibrary")}
        </MenuItem> */}
        {/* {showVideoLibrary && (
          <SubMenu>
            <SubMenuLink
              to="/questionsAndFears"
              onClick={handleMenuClick}
              className={
                location.pathname === "/questionsAndFears" ? "active" : ""
              }
            >
              {t("menu.questionsAndFears")}
            </SubMenuLink>
            <SubMenuLink
              to="/treatments"
              onClick={handleMenuClick}
              className={location.pathname === "/treatments" ? "active" : ""}
            >
              {t("menu.treatments")}
            </SubMenuLink>
            <SubMenuLink
              to="/prevention"
              onClick={handleMenuClick}
              className={location.pathname === "/prevention" ? "active" : ""}
            >
              {t("menu.prevention")}
            </SubMenuLink>
          </SubMenu>
        )} */}

        <MenuLink
          to="/payment"
          onClick={handleMenuClick}
          className={location.pathname === "/payment" ? "active" : ""}
        >
          {t("menu.payment")}
        </MenuLink>

        <MenuLink
          to="/OurPatientsThoughts"
          onClick={handleMenuClick}
          className={
            location.pathname === "/OurPatientsThoughts" ? "active" : ""
          }
        >
          {t("menu.patientReviews")}
        </MenuLink>

        <MenuLink
          to="/contactUs"
          onClick={handleMenuClick}
          className={location.pathname === "/contactUs" ? "active" : ""}
        >
          {t("menu.contactUs")}
        </MenuLink>
      </Sidebar>
      <ContentArea className="ContentArea">
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/meetDrPonce" element={<MeetDrPonce />} />
          <Route path="/WhatWeDo" element={<WhatWeDo />} />
          <Route path="/whatWeDoTreatments" element={<WhatWeDoTreatments />} />
          <Route path="/telemedicine" element={<Telemedicine />} />
          <Route path="/weightManagement" element={<WeightManagement />} />
          <Route
            path="/cardiacRehabilitation"
            element={<CardiacRehabilitation />}
          />
          <Route path="/ThisIsUs" element={<ThisIsUs />} />
          <Route path="/QuestionsAndFears" element={<QuestionsAndFears />} />
          <Route path="/Treatments" element={<Treatments />} />
          <Route path="/Prevention" element={<Prevention />} />
          <Route path="/payment" element={<InsuranceAndPayments />} />
          <Route
            path="/OurPatientsThoughts"
            element={<OurPatientsThoughts />}
          />
          <Route path="/ContactUs" element={<ContactUs />} />
        </Routes>
      </ContentArea>
    </PageLayout>
  );
};

export default Menu;
